import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert
} from '@mui/material';
import DroneImageUpload from './DroneImageUpload';

const DRONE_MANUFACTURERS = [
  'DJI',
  'Autel Robotics',
  'Skydio',
  'Parrot',
  'Yuneec',
  'Freefly',
  'Insitu',
  'senseFly',
  'Other'
];

const DRONE_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'maintenance', label: 'In Maintenance' },
  { value: 'retired', label: 'Retired' },
  { value: 'lost', label: 'Lost' }
];

const AddDroneDialog = ({ open, onClose, onAdd, organizationName }) => {
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    serialNumber: '',
    registrationNumber: '',
    nickname: '',
    status: 'active',
    purchaseDate: '',
    notes: '',
    imageUrl: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleImageUpload = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      imageUrl: imageUrl || ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.manufacturer.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }
    if (!formData.model.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = 'Serial number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onAdd(formData);
      // Reset form on success
      setFormData({
        manufacturer: '',
        model: '',
        serialNumber: '',
        registrationNumber: '',
        nickname: '',
        status: 'active',
        purchaseDate: '',
        notes: '',
        imageUrl: ''
      });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        manufacturer: '',
        model: '',
        serialNumber: '',
        registrationNumber: '',
        nickname: '',
        status: 'active',
        purchaseDate: '',
        notes: '',
        imageUrl: ''
      });
      setErrors({});
      setSubmitError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Add New Drone</Typography>
        <Typography variant="body2" color="text.secondary">
          Add a drone to {organizationName}'s fleet
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Manufacturer */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.manufacturer}>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={formData.manufacturer}
                onChange={handleChange('manufacturer')}
                label="Manufacturer"
              >
                {DRONE_MANUFACTURERS.map((manufacturer) => (
                  <MenuItem key={manufacturer} value={manufacturer}>
                    {manufacturer}
                  </MenuItem>
                ))}
              </Select>
              {errors.manufacturer && (
                <Typography variant="caption" color="error">
                  {errors.manufacturer}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Model */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Model"
              value={formData.model}
              onChange={handleChange('model')}
              error={!!errors.model}
              helperText={errors.model || 'e.g., Mavic 3, Phantom 4 Pro'}
            />
          </Grid>

          {/* Serial Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Serial Number"
              value={formData.serialNumber}
              onChange={handleChange('serialNumber')}
              error={!!errors.serialNumber}
              helperText={errors.serialNumber || 'Must be unique'}
            />
          </Grid>

          {/* Registration Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="FAA Registration Number"
              value={formData.registrationNumber}
              onChange={handleChange('registrationNumber')}
              helperText="FAA registration (if applicable)"
            />
          </Grid>

          {/* Nickname */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nickname"
              value={formData.nickname}
              onChange={handleChange('nickname')}
              helperText="User-friendly name (e.g., Unit 01)"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Status"
              >
                {DRONE_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Purchase Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Purchase Date"
              value={formData.purchaseDate}
              onChange={handleChange('purchaseDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <DroneImageUpload
              currentImageUrl={formData.imageUrl}
              onImageUpload={handleImageUpload}
              disabled={submitting}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              helperText="Additional notes about this drone"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Adding...' : 'Add Drone'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDroneDialog;
